import { BasicFieldInputs } from '../components/BasicFieldInputs';
import { AddRemoveItemList } from '../components/AddRemoveItemList';
import {
  Button,
  Divider,
  Title,
  TextInput,
  Stack,
  Flex,
  Box,
  Text,
  Paper,
} from '@mantine/core';
import { IconCirclePlus, IconTrash } from '@tabler/icons-react';
import { toInputName } from '../utils/toInputName';
import { useIntl } from 'react-intl';

export const MultiLevelComboboxField = ({
  field,
  setField,
}: {
  field: any;
  setField: (field: any) => void;
}) => {
  const intl = useIntl();
  return (
    <Stack gap="md">
      <BasicFieldInputs
        fields={['label']}
        fieldValues={field}
        requiredFields={['label']}
        onChange={(key, value) => setField({ ...field, [key]: value })}
      />

      <Divider />

      <Stack gap="md">
        <Flex gap="md" align="end">
          <Box style={{ flex: 1 }}>
            <TextInput
              value={field.tempSelectedValueLabel || ''}
              name="levelLabel"
              label={intl.formatMessage({ id: 'levelLabel' })}
              onChange={(event) =>
                setField({
                  ...field,
                  tempSelectedValueLabel: event.currentTarget.value,
                })
              }
            />
          </Box>
          <Button
            variant="default"
            onClick={() => {
              if (field.tempSelectedValueLabel) {
                const normalizedKey = toInputName(field.tempSelectedValueLabel);
                setField({
                  ...field,
                  customInput: {
                    ...field.customInput,
                    selectedValues: {
                      ...(field.customInput?.selectedValues || {}),
                      [normalizedKey]: '',
                    },
                    selectedValuesLabels: {
                      ...(field.customInput?.selectedValuesLabels || {}),
                      [normalizedKey]: field.tempSelectedValueLabel,
                    },
                  },
                  tempSelectedValueLabel: '',
                });
              }
            }}
            disabled={!field.tempSelectedValueLabel}
          >
            <IconCirclePlus size={16} />
          </Button>
        </Flex>
        <Stack gap="md">
          {Object.keys(field.customInput?.selectedValues || {}).map(
            (key, index) => (
              <Flex key={index} gap="md" align="center" justify="space-between">
                <Stack gap={4}>
                  <Flex align="center">
                    <Text fw={700} mr={8}>
                      {field.customInput?.selectedValuesLabels?.[key] || key}
                    </Text>
                  </Flex>
                  <Text size="xs" c="dimmed">
                    Key: {key}
                  </Text>
                </Stack>
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => {
                    const keys = Object.keys(
                      field.customInput?.selectedValues || {}
                    );
                    const keyToRemove = keys[index];
                    const newSelectedValues = {
                      ...field.customInput?.selectedValues,
                    };
                    const newSelectedValuesLabels = {
                      ...field.customInput?.selectedValuesLabels,
                    };
                    delete newSelectedValues[keyToRemove];
                    delete newSelectedValuesLabels[keyToRemove];
                    setField({
                      ...field,
                      customInput: {
                        ...field.customInput,
                        selectedValues: newSelectedValues,
                        selectedValuesLabels: newSelectedValuesLabels,
                      },
                    });
                  }}
                >
                  <IconTrash size={16} />
                </Button>
              </Flex>
            )
          )}
        </Stack>
      </Stack>

      <AddRemoveItemList
        items={Object.keys(field.customInput?.properties || {}) || []}
        inputLabel={intl.formatMessage({ id: 'propertyKey' })}
        inputValue={field.tempPropertyKey || ''}
        onInputChange={(value) =>
          setField({ ...field, tempPropertyKey: value })
        }
        onAddItem={() => {
          if (field.tempPropertyKey) {
            const normalizedKey = toInputName(field.tempPropertyKey);
            setField({
              ...field,
              customInput: {
                ...field.customInput,
                properties: {
                  ...(field.customInput?.properties || {}),
                  [normalizedKey]: '',
                },
              },
              tempPropertyKey: '',
            });
          }
        }}
        onRemoveItem={(index) => {
          const keys = Object.keys(field.customInput?.properties || {});
          const keyToRemove = keys[index];
          const newProperties = { ...field.customInput?.properties };
          delete newProperties[keyToRemove];
          setField({
            ...field,
            customInput: {
              ...field.customInput,
              properties: newProperties,
            },
          });
        }}
        addButtonDisabled={!field.tempPropertyKey}
      />

      {(Object.keys(field.customInput?.selectedValues || {}).length > 0 ||
        Object.keys(field.customInput?.properties || {}).length > 0) && (
        <>
          <Title order={4}>{intl.formatMessage({ id: 'dataEntries' })}</Title>
          <Stack gap="md">
            <Stack gap="xs">
              {Object.keys(field.customInput?.selectedValues || {}).map(
                (key) => (
                  <TextInput
                    key={key}
                    value={field.tempEntryData?.[key] || ''}
                    name={key}
                    label={
                      field.customInput?.selectedValuesLabels?.[key] || key
                    }
                    onChange={(event) =>
                      setField({
                        ...field,
                        tempEntryData: {
                          ...(field.tempEntryData || {}),
                          [key]: event.currentTarget.value,
                        },
                      })
                    }
                  />
                )
              )}
              {Object.keys(field.customInput?.properties || {}).map((key) => (
                <TextInput
                  key={key}
                  value={field.tempEntryData?.[key] || ''}
                  name={key}
                  label={key}
                  onChange={(event) =>
                    setField({
                      ...field,
                      tempEntryData: {
                        ...(field.tempEntryData || {}),
                        [key]: event.currentTarget.value,
                      },
                    })
                  }
                />
              ))}
            </Stack>
            <Button
              variant="default"
              onClick={() => {
                const allKeys = [
                  ...Object.keys(field.customInput?.selectedValues || {}),
                  ...Object.keys(field.customInput?.properties || {}),
                ];
                const hasAllValues = allKeys.every(
                  (key) => field.tempEntryData?.[key]
                );

                if (hasAllValues && field.tempEntryData) {
                  setField({
                    ...field,
                    customInput: {
                      ...field.customInput,
                      values: [
                        ...(field.customInput?.values || []),
                        field.tempEntryData,
                      ],
                    },
                    tempEntryData: {},
                  });
                }
              }}
              disabled={
                !field.tempEntryData ||
                ![
                  ...Object.keys(field.customInput?.selectedValues || {}),
                  ...Object.keys(field.customInput?.properties || {}),
                ].every((key) => field.tempEntryData?.[key])
              }
            >
              Add Entry
            </Button>
          </Stack>

          <Stack gap="md">
            {field.customInput?.values?.map((entry: any, index: number) => (
              <Paper key={index} p="sm" withBorder>
                <Flex gap="md" align="center" justify="space-between">
                  <Stack gap={4}>
                    {Object.entries(entry).map(([key, value]) => (
                      <Flex key={key} align="center">
                        <Text fw={700} mr={8}>
                          {field.customInput?.selectedValuesLabels?.[key] ||
                            key}
                          :
                        </Text>
                        <Text>{value as string}</Text>
                      </Flex>
                    ))}
                  </Stack>
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => {
                      setField({
                        ...field,
                        customInput: {
                          ...field.customInput,
                          values: field.customInput?.values.filter(
                            (_: any, i: number) => i !== index
                          ),
                        },
                      });
                    }}
                  >
                    <IconTrash size={16} color="red" />
                  </Button>
                </Flex>
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
};
